// Universal Approval Status Dashboard
//
// Overview of every approval item across all modules by status. Exposes
// the "Submit New Approval Item" form for instant test submissions and day-to-day work.
// Route: /line-item-approval/dashboard

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Plus, X, RefreshCw, Download, Layers } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_TABS = [
  { code: '', label: 'All Statuses' },
  { code: 'draft', label: 'Draft' },
  { code: 'pending', label: 'Pending' },
  { code: 'sent_back', label: 'Sent Back' },
  { code: 'approved', label: 'Approved' },
  { code: 'rejected', label: 'Rejected' },
  { code: 'processed', label: 'Processed' },
  { code: 'locked', label: 'Locked' },
];

const STATUS_CLASS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  sent_back: 'bg-orange-50 text-orange-700 border-orange-200',
  payroll_accepted: 'bg-sky-50 text-sky-700 border-sky-200',
  processed: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  locked: 'bg-slate-700 text-white border-slate-800',
};

function fmtINR(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function LineItemApprovalDashboard() {
  const [meta, setMeta] = useState({ modules: [], approver_types: [], input_types: [], input_type_labels: {} });
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    module: 'monthly_inputs', input_type: 'monthly_inputs', employee_id: '', amount: '', description: '', submit_now: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API_URL}/line-item-approval/meta`, { withCredentials: true });
        setMeta(m.data || {});
        const c = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(c.data || []);
        if ((c.data || []).length) setSelCompany(c.data[0].id);
      } catch (e) {
        toast.error('Failed to load dashboard metadata');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!selCompany) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ company_id: selCompany });
      if (status) q.set('status', status);
      if (selectedModule) q.set('module', selectedModule);
      if (month) q.set('payroll_month', month);
      const { data } = await axios.get(`${API_URL}/line-item-approval/items?${q.toString()}`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error('Failed to load dashboard items');
    } finally {
      setLoading(false);
    }
  }, [selectedModule, month, selCompany, status]);

  useEffect(() => {
    if (!selCompany) return;
    (async () => {
      try {
        const e = await axios.get(`${API_URL}/employees?company_id=${selCompany}&limit=1000`, { withCredentials: true });
        setEmployees(e.data || []);
      } catch {
        setEmployees([]);
      }
      await load();
    })();
  }, [selCompany, load]);

  useEffect(() => {
    load();
  }, [status, selectedModule, month, load]);

  const submitNew = async () => {
    if (!form.employee_id) return toast.error('Employee is required');
    if (form.amount === '' || Number.isNaN(Number(form.amount))) return toast.error('Amount is required');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/line-item-approval/items`, {
        company_id: selCompany,
        module: form.module,
        input_type: form.module,
        employee_id: form.employee_id,
        amount: Number(form.amount),
        payroll_month: month,
        description: form.description,
        submit_now: !!form.submit_now,
      }, { withCredentials: true });
      toast.success('Approval request created');
      setShowNew(false);
      setForm({ module: 'monthly_inputs', input_type: 'monthly_inputs', employee_id: '', amount: '', description: '', submit_now: true });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    const s = {};
    for (const it of items) {
      const k = it.status;
      if (!s[k]) s[k] = { count: 0, amount: 0 };
      s[k].count += 1;
      s[k].amount += Number(it.current_amount || 0);
    }
    return s;
  }, [items]);

  const moduleLabel = (mCode) => {
    const found = (meta.modules || []).find((m) => m.code === mCode);
    return found ? found.label : meta.input_type_labels[mCode] || mCode;
  };

  const exportCsv = () => {
    const rows = [
      ['Employee Code', 'Employee Name', 'Module', 'Amount', 'Month', 'Status', 'Current Level', 'Description'].join(','),
    ];
    for (const it of items) {
      const lvls = it.levels_snapshot || [];
      const cur = it.current_level ?? 0;
      const lvlName = (cur < lvls.length) ? (lvls[cur].name || '') : '';
      rows.push([
        JSON.stringify(it.employee_code || ''),
        JSON.stringify(it.employee_name || ''),
        JSON.stringify(moduleLabel(it.module || it.input_type)),
        Number(it.current_amount || 0),
        it.payroll_month || '',
        it.status,
        JSON.stringify(lvlName),
        JSON.stringify(it.description || ''),
      ].join(','));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `universal_approvals_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <div className="p-6 bg-slate-50 min-h-screen" data-testid="universal-dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><Layers className="w-5 h-5" /></span>
              <h1 className="text-xl font-bold text-slate-900">Universal Approval Dashboard</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Track approval requests across all modules (New Joiners, Resignations, Monthly Inputs, Claims, Revisions, Exit Clearance, etc.).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg px-3.5 py-2 text-xs border border-slate-200"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg px-3.5 py-2 text-xs border border-slate-200"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg px-4 py-2 text-xs shadow-sm"
            >
              <Plus className="w-4 h-4" /> New Approval Request
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Company</label>
            <select
              value={selCompany}
              onChange={(e) => setSelCompany(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white font-medium"
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Payroll Month / Period</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-medium"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Approval Module</label>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-slate-50 font-medium"
            >
              <option value="">All Approval Modules</option>
              {(meta.modules || []).map((m) => (
                <option key={m.code} value={m.code}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Status Filter</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white font-medium"
            >
              {STATUS_TABS.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {/* Status Summary Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {Object.keys(summary).sort().map((s) => (
            <div key={s} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_CLASS[s] || 'bg-slate-100'}`}>
                {s}
              </span>
              <div className="text-xl font-bold text-slate-900 mt-2">{summary[s].count}</div>
              <div className="text-[10px] font-mono text-slate-500 mt-0.5">{fmtINR(summary[s].amount)}</div>
            </div>
          ))}
        </div>

        {/* Listing */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-5 py-3">Employee</th>
                <th className="px-5 py-3">Module</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3">Month</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Current Level</th>
                <th className="px-5 py-3">Rule Applied</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={7} className="p-8 text-center text-slate-500 font-medium">Loading items...</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center text-slate-400 font-medium">No items match the selected filters.</td></tr>
              )}
              {items.map((it) => {
                const lvls = it.levels_snapshot || [];
                const cur = it.current_level ?? 0;
                const lvlName = (cur < lvls.length) ? lvls[cur].name : 'Final';
                return (
                  <tr key={it.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-5 py-3 font-medium text-slate-900">
                      {it.employee_name}
                      <div className="text-xs text-slate-500 font-mono">{it.employee_code}</div>
                    </td>
                    <td className="px-5 py-3 font-semibold text-slate-700">
                      {moduleLabel(it.module || it.input_type)}
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">{fmtINR(it.current_amount)}</td>
                    <td className="px-5 py-3 text-xs text-slate-600 font-medium">{it.payroll_month}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${STATUS_CLASS[it.status] || 'bg-slate-100'}`}>
                        {it.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-800 font-medium">
                      {lvlName}
                      <div className="text-[10px] text-slate-400 font-mono">Step {cur + 1} of {lvls.length}</div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">{it.matched_workflow_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* New Item Modal */}
        {showNew && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">New Approval Request</h3>
                <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Approval Module *</label>
                  <select
                    value={form.module}
                    onChange={(e) => setForm({ ...form, module: e.target.value, input_type: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium"
                  >
                    {(meta.modules || []).map((m) => (
                      <option key={m.code} value={m.code}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Employee *</label>
                  <select
                    value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium"
                  >
                    <option value="">— Select Employee —</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>{`${emp.employee_code} · ${emp.personal_details?.full_name || emp.full_name || '—'}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Amount (₹) *</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Description / Reason</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <button onClick={() => setShowNew(false)} className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-lg">Cancel</button>
                <button
                  disabled={saving}
                  onClick={submitNew}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 text-xs font-bold rounded-lg shadow-sm disabled:opacity-50"
                >
                  {saving ? 'Creating…' : 'Submit for Approval'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
